import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@design-system';
import { useToast } from '@client/components/hooks/use-toast';
import { selectSessionAdvisor } from '@client/model/selectors';
import { distributionPartnersApi } from '@client/api/waterlily';

interface LegacyShareAttestationDialogProps {
  clientId: string;
  open: boolean;
  onConfirmed: () => void;
  onCancel: () => void;
}

// Gate shown before sharing a legacy (pre new-consent) client. The advisor must attest they hold
// the consents required to share this client's data; confirming records the attestation, then the
// caller proceeds with the share.
export function LegacyShareAttestationDialog({
  clientId,
  open,
  onConfirmed,
  onCancel,
}: LegacyShareAttestationDialogProps) {
  const { toast } = useToast();
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const orgLegalName = sessionAdvisor?.organizationName || 'your firm';
  const [attested, setAttested] = useState(false);

  useEffect(() => {
    if (open) setAttested(false);
  }, [open]);

  const { mutate, isPending } = useMutation({
    mutationFn: () => distributionPartnersApi.recordLegacyShareAttestation(clientId, true),
    onSuccess: () => onConfirmed(),
    onError: err => {
      toast({
        title: 'Could not record your attestation',
        description: err instanceof Error ? err.message : 'Something went wrong. Please try again.',
        variant: 'destructive',
        className: '-translate-y-5',
      });
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => !isOpen && !isPending && onCancel()}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-primary-dark">
            Do you have consent to share this client with your distribution partner(s)?
          </DialogTitle>
          <DialogDescription className="text-foreground">
            This client joined before June 17, 2026 and consented to share their data with you and{' '}
            {orgLegalName}, but not with other partners you collaborate with. To share this client,
            confirm below that you have their consent to share their data with your distribution
            partner(s).
          </DialogDescription>
        </DialogHeader>

        <label
          htmlFor="legacy-share-attestation"
          className="bg-muted/50 hover:bg-muted flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors"
        >
          <Checkbox
            id="legacy-share-attestation"
            checked={attested}
            onCheckedChange={checked => setAttested(checked === true)}
            className="mt-0.5"
          />
          <Label
            htmlFor="legacy-share-attestation"
            className="text-foreground cursor-pointer text-sm font-normal"
          >
            By checking this box, I represent and warrant that, for this client whose data I am
            choosing to share, I have obtained and currently hold the appropriate consents required
            under applicable law to share that client's personal data with third party partners such
            as my distribution partner.
          </Label>
        </label>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isPending}
          >
            Not now
          </Button>
          <Button
            onClick={() => mutate()}
            disabled={!attested || isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-1 size-4 animate-spin" />
                Confirming…
              </>
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
